import "./Message.scss";
import React, {useState} from "react";
import {Avatar, Box, IconButton, LinearProgress, Paper, Stack, Tooltip, Typography} from "@mui/material";
import {Check, ContentCopyOutlined, ThumbDownOutlined, ThumbUpOutlined} from "@mui/icons-material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type {SyntaxHighlighterProps} from "react-syntax-highlighter";
import {Prism} from "react-syntax-highlighter";
import {oneDark, oneLight} from "react-syntax-highlighter/dist/esm/styles/prism";
import {useTheme} from "@mui/material/styles";
import type {ChatMessage, ConsolidationToolProgress} from "../types/chatTypes";
import {ChartRenderer} from "./ChartRenderer";
import {InitiativeDraftCard} from "~/sit/chat/InitiativeDraftCard";
import {FeedbackModal} from "./FeedbackModal";
import {useChatContext} from "../context/ChatContext";
import {useStores} from "~/stores";
import DataRetrievalIcon from "../../../../public/chat/data_retreival.svg?react";
import CreatingReportIcon from "../../../../public/chat/creating_report.svg?react";
import ExportIcon from "../../../../public/chat/export.svg?react";
import InternetSearchIcon from "../../../../public/chat/internet_search.svg?react";
import DotsIcon from "../../../../public/chat/dots.svg?react";
import TickIcon from "../../../../public/chat/Tick.svg?react";
import SavingsPigIcon from '~/components/icons/svg/SavingsPigIcon.svg?react';

const SyntaxHighlighter =
    Prism as never as typeof React.Component<SyntaxHighlighterProps>;

type Props = {
    message: ChatMessage;
};

type StepVisual = {
    text: {function_call: string; function_response: string};
    icon: React.FC<React.SVGProps<SVGSVGElement>>;
};

// Default fallback
const defaultConfig: StepVisual = {
    text: {
        function_call: "Processing…",
        function_response: "Processing completed",
    },
    icon: DataRetrievalIcon,
};

// Function name to text and icon mapping
const functionConfig: Record<string, StepVisual> = {
    execute_sql: {
        text: {
            function_call: "Data retrieval in progress",
            function_response: "Data retrieval completed",
        },
        icon: DataRetrievalIcon,
    },
    create_report: {
        text: {
            function_call: "Report generation in progress",
            function_response: "Report generation completed",
        },
        icon: CreatingReportIcon,
    },
    export_data: {
        text: {
            function_call: "Export in progress",
            function_response: "Export ready",
        },
        icon: ExportIcon,
    },
    internet_search: {
        text: {
            function_call: "Internet search in progress",
            function_response: "Internet search completed",
        },
        icon: InternetSearchIcon,
    },
    consolidate_suppliers: {
        text: {
            function_call: "Supplier consolidation in progress",
            function_response: "Supplier consolidation completed",
        },
        icon: CreatingReportIcon,
    },
    consolidate_all_tail_suppliers: {
        text: {
            function_call: "Tail spend consolidation in progress",
            function_response: "Tail spend consolidation completed",
        },
        icon: CreatingReportIcon,
    },
    list_levers: {
        text: {
            function_call: "Reading levers…",
            function_response: "Levers loaded",
        },
        icon: SavingsPigIcon,
    },
    list_initiatives: {
        text: {
            function_call: "Analyzing initiatives…",
            function_response: "Analysis of initiatives completed",
        },
        icon: SavingsPigIcon,
    },
    propose_initiative: {
        text: {
            function_call: "Preparing initiative…",
            function_response: "Initiative ready to review",
        },
        icon: SavingsPigIcon,
    },
    create_initiative: {
        text: {
            function_call: "Creating initiative…",
            function_response: "Initiative created",
        },
        icon: SavingsPigIcon,
    },
};

const TAIL_SPEND_CONSOLIDATION_TOOL_NAMES = new Set([
    "consolidate_suppliers",
    "consolidate_all_tail_suppliers",
]);

const MACRO_STAGE_FALLBACK: Record<string, number> = {
    opportunities: 1,
    initiatives: 2,
    persist: 3,
    complete: 4,
    done: 4,
};

function macroStageNumber(
    tp: Pick<ConsolidationToolProgress, "stage" | "stageNumber">,
): number {
    if (tp.stageNumber != null && tp.stageNumber > 0) return tp.stageNumber;
    return MACRO_STAGE_FALLBACK[tp.stage] ?? 1;
}

function macroStageTotal(
    tp: Pick<ConsolidationToolProgress, "stageTotal">,
): number {
    return tp.stageTotal != null && tp.stageTotal > 0 ? tp.stageTotal : 4;
}

export const Message: React.FC<Props> = React.memo(({message}) => {
    const theme = useTheme();
    const {currentSession} = useChatContext();
    const {chatApiStore} = useStores();
    const [copied, setCopied] = useState(false);

    const [feedbackUpLoading, setFeedbackUpLoading] = useState(false);
    const [feedbackDownLoading, setFeedbackDownLoading] = useState(false);
    const [feedbackGiven, setFeedbackGiven] = useState<"up" | "down" | null>(
        null,
    );

    const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
    const isUser = message.type === "user";
    const isFunction =
        message.event_type === "function_call" ||
        message.event_type === "function_response";

    // Extract function name from content (assuming it's the content itself or parse if needed)
    const functionName = isFunction ? message.content : "";

    // Get the config for this function
    const config =
        functionName && functionConfig[functionName]
            ? functionConfig[functionName]
            : defaultConfig;

    // Handle transitioning state - show previous event type during animation
    const currentEventType =
        message.isTransitioning && message.previousEventType
            ? message.previousEventType
            : message.event_type;

    // Get the appropriate text based on event type
    const functionText =
        currentEventType === "function_call"
            ? config.text.function_call
            : config.text.function_response;

    // Get the status indicator based on event type
    const StatusIndicator =
        currentEventType === "function_call" ? DotsIcon : TickIcon;

    // Handle copy to clipboard
    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(message.content);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error("Failed to copy text:", err);
        }
    };

    // Handle thumbs up
    const handleThumbsUp = async () => {
        if (!currentSession) return;
        setFeedbackUpLoading(true);
        try {
            await chatApiStore.saveFeedback(
                currentSession.id,
                message.id,
                message.invocationId ?? "",
                true,
                null,
                null,
            );
            setFeedbackGiven("up");
        } finally {
            setFeedbackUpLoading(false);
        }
    };

    // Handle thumbs down - open modal
    const handleThumbsDown = () => {
        if (!currentSession) return;
        setFeedbackModalOpen(true);
    };

    // Handle feedback modal submit
    const handleFeedbackSubmit = async (
        category: string | null,
        description: string | null,
    ) => {
        if (!currentSession) return;
        setFeedbackDownLoading(true);
        try {
            await chatApiStore.saveFeedback(
                currentSession.id,
                message.id,
                message.invocationId ?? "",
                false,
                category,
                description,
            );
            setFeedbackGiven("down");
            setFeedbackModalOpen(false);
        } finally {
            setFeedbackDownLoading(false);
        }
    };

    // Custom components for markdown rendering (memoized to prevent unnecessary re-renders)
    const components = React.useMemo(
        () => ({
            code({className, children, ...props}: any) {
                const match = /language-(\w+)/.exec(className || "");
                const content = String(children).replace(/\n$/, "");

                const isInline = !className && !content.includes("\n");

                // Handle chart rendering for non-inline code blocks
                if (!isInline) {
                    // Check for fenced code blocks with language specification
                    if (match) {
                        const language = match[1];

                        // Check if this is a chart block
                        if (language === "chart" || language === "echarts") {
                            return <ChartRenderer config={content}/>;
                        }

                        // Regular syntax highlighting for other code blocks
                        return (
                            <SyntaxHighlighter
                                style={theme.palette.mode === "dark" ? oneDark : oneLight}
                                language={language}
                                PreTag="div"
                                {...props}
                            >
                                {content}
                            </SyntaxHighlighter>
                        );
                    }

                    // Check for chart content in regular code blocks (no language specified)
                    // This handles indented code blocks that might contain chart JSON
                    try {
                        const trimmedContent = content.trim();
                        if (
                            trimmedContent.startsWith("{") &&
                            trimmedContent.endsWith("}")
                        ) {
                            const parsed = JSON.parse(trimmedContent);
                            // Check if this looks like an ECharts configuration
                            if (
                                parsed &&
                                (parsed.series || parsed.xAxis || parsed.yAxis || parsed.title)
                            ) {
                                return <ChartRenderer config={trimmedContent}/>;
                            }
                        }
                    } catch (e) {
                        // Not valid JSON, continue with regular code rendering
                    }

                    // Regular code block without language specification
                    return (
                        <SyntaxHighlighter
                            style={theme.palette.mode === "dark" ? oneDark : oneLight}
                            language="javascript"
                            PreTag="div"
                            {...props}
                        >
                            {content}
                        </SyntaxHighlighter>
                    );
                }

                // Inline code
                return (
                    <Box
                        component="code"
                        sx={{
                            display: "inline", // Make sure it's inline to flow with text
                            backgroundColor: (theme) =>
                                theme.palette.mode === "dark" ? "#0d0d0d" : "#f1f5f9",
                            color: (theme) =>
                                theme.palette.mode === "dark" ? "#a78bfa" : "#7c3aed",
                            px: 0.3,
                            py: 0.2,
                            borderRadius: 0.5,
                            fontFamily:
                                "JetBrains Mono, Fira Code, SF Mono, Monaco, Consolas, Ubuntu Mono, monospace",
                            fontSize: "0.9em",
                            border: (theme) =>
                                theme.palette.mode === "dark"
                                    ? "1px solid rgba(255,255,255,0.06)"
                                    : "1px solid rgba(0,0,0,0.06)",
                            fontWeight: 500,
                            whiteSpace: "nowrap", // Prevent wrapping within the code element
                        }}
                        {...props}
                    >
                        {children}
                    </Box>
                );
            },
            // Open links (e.g. report downloads) in a new tab so the chat page is kept
            a: ({href, children}: {href?: string; children?: React.ReactNode}) => (
                <a href={href} target="_blank" rel="noopener noreferrer">
                    {children}
                </a>
            ),
            p: ({children}: any) => (
                <Typography
                    variant="body1"
                    component="span"
                    sx={{mb: 1.5, wordWrap: "break-word"}}
                >
                    {children}
                </Typography>
            ),
            ul: ({children}: any) => (
                <Box component="ul" sx={{m: 1.5, pl: 2.5}}>
                    {children}
                </Box>
            ),
            ol: ({children}: any) => (
                <Box component="ol" sx={{m: 1.5, pl: 2.5}}>
                    {children}
                </Box>
            ),
            li: ({children}: any) => (
                <Typography
                    component="li"
                    variant="body1"
                    sx={{mb: 0.75, wordWrap: "break-word"}}
                >
                    {children}
                </Typography>
            ),
            blockquote: ({children}: any) => (
                <Paper
                    sx={{
                        borderLeft: "4px solid #00d4aa",
                        backgroundColor: (theme) =>
                            theme.palette.mode === "dark" ? "#141414" : "#f8fafc",
                        borderRadius: "0 12px 12px 0",
                        p: 2,
                        m: 2,
                        fontStyle: "italic",
                        color: "text.secondary",
                        border: (theme) =>
                            theme.palette.mode === "dark"
                                ? "1px solid rgba(255,255,255,0.04)"
                                : "1px solid rgba(0,0,0,0.04)",
                    }}
                >
                    {children}
                </Paper>
            ),
            h1: ({children}: any) => (
                <Typography variant="h4" sx={{mt: 1, mb: 1.5, fontWeight: 600}}>
                    {children}
                </Typography>
            ),
            h2: ({children}: any) => (
                <Typography variant="h5" sx={{mt: 1, mb: 1.5, fontWeight: 600}}>
                    {children}
                </Typography>
            ),
            h3: ({children}: any) => (
                <Typography variant="h6" sx={{mt: 1, mb: 1.5, fontWeight: 600}}>
                    {children}
                </Typography>
            ),
            h4: ({children}: any) => (
                <Typography
                    variant="subtitle1"
                    sx={{mt: 1, mb: 1.5, fontWeight: 600}}
                >
                    {children}
                </Typography>
            ),
            h5: ({children}: any) => (
                <Typography
                    variant="subtitle2"
                    sx={{mt: 1, mb: 1.5, fontWeight: 600}}
                >
                    {children}
                </Typography>
            ),
            h6: ({children}: any) => (
                <Typography variant="body1" sx={{mt: 1, mb: 1.5, fontWeight: 600}}>
                    {children}
                </Typography>
            ),
        }),
        [theme],
    );

    // const userMarkdownComponents = React.useMemo(
    //     () => ({
    //         code({children, ...props}: {children?: React.ReactNode}) {
    //             return (
    //                 <Box
    //                     component="code"
    //                     sx={{
    //                         display: "inline",
    //                         backgroundColor: "rgba(255, 255, 255, 0.12)",
    //                         color: "#E8E8F0",
    //                         px: 0.5,
    //                         py: 0.15,
    //                         borderRadius: 0.5,
    //                         fontFamily:
    //                             "JetBrains Mono, Fira Code, SF Mono, Monaco, Consolas, monospace",
    //                         fontSize: "0.9em",
    //                         fontWeight: 500,
    //                     }}
    //                     {...props}
    //                 >
    //                     {children}
    //                 </Box>
    //             );
    //         },
    //         p: ({children}: {children?: React.ReactNode}) => (
    //             <Typography
    //                 variant="body1"
    //                 component="span"
    //                 sx={{color: "#F0F0F7", wordWrap: "break-word"}}
    //             >
    //                 {children}
    //             </Typography>
    //         ),
    //         strong: ({children}: {children?: React.ReactNode}) => (
    //             <Box component="strong" sx={{color: "#F0F0F7", fontWeight: 600}}>
    //                 {children}
    //             </Box>
    //         ),
    //     }),
    //     [],
    // );

    return (
        <Box
            className="chat-message message-fade-in"
            sx={{
                display: "flex",
                gap: 2,
                mb: 2,
                maxWidth: "100%",
                flexDirection: isUser ? "row-reverse" : "row",
            }}
        >
            <Stack
                alignItems={isUser ? "flex-end" : message.initiativeProposal ? "stretch" : "flex-start"}
                justifyContent={isFunction ? "center" : "flex-start"}
                sx={{
                    flex: 1,
                    minWidth: 0,
                    width: message.initiativeProposal ? "100%" : undefined,
                }}
            >
                <Box
                    className="message-container"
                    sx={{
                        position: "relative",
                        width: message.initiativeProposal ? "100%" : undefined,
                    }}
                >
                    {isUser ? (
                        <Paper
                            sx={{
                                maxWidth: "500px",
                                mb: 1.5,
                                backgroundColor: "#2F3036",
                                borderRadius: 3,
                                py: "10px",
                                px: "20px",
                            }}
                        >
                            <Box className="user-message-text" sx={{color: "#F0F0F7", p: 0}}>
                                {/* <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={userMarkdownComponents}
                                >
                                    {userMessageContentToMarkdown(message.content)}
                                </ReactMarkdown> */}
                                {message.content}
                            </Box>
                        </Paper>
                    ) : isFunction ? (
                        <Stack direction="row" alignItems="center" gap={2}>
                            <Box sx={{position: "relative"}}>
                                <Avatar
                                    className={`chat-avatar ${
                                        message.event_type === "function_call"
                                            ? "function-call-avatar"
                                            : "function-response-avatar"
                                    }`}
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        border: "1px solid !important",
                                        borderColor: "background.paper",
                                    }}
                                >
                                    {React.createElement(config.icon, {
                                        style: {
                                            width: 40,
                                            height: 40,
                                        },
                                    })}
                                </Avatar>
                                <Box
                                    sx={{
                                        position: "absolute",
                                        bottom: 0,
                                        right: 0,
                                        width: 16,
                                        height: 16,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <StatusIndicator
                                        style={{
                                            width: 16,
                                            height: 16,
                                        }}
                                    />
                                </Box>
                            </Box>

                            <Typography
                                variant="body1"
                                sx={{
                                    color: "text.primary",
                                    fontWeight: 500,
                                }}
                            >
                                {functionText}
                            </Typography>
                            {isFunction &&
                                currentEventType === "function_call" &&
                                TAIL_SPEND_CONSOLIDATION_TOOL_NAMES.has(functionName) && (
                                <Box sx={{mt: 0, width: "100%", minWidth: 0}}>
                                    {message.toolProgress &&
                                        message.toolProgress.stage !== "done" &&
                                        message.toolProgress.total > 0 ? (
                                            <>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={Math.min(
                                                        100,
                                                        (message.toolProgress.completed /
                                                            message.toolProgress.total) *
                                                        100,
                                                    )}
                                                    sx={{height: 8, borderRadius: 1}}
                                                />
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{display: "block", mt: 0.75}}
                                                >
                                                    Stage {macroStageNumber(message.toolProgress)}/
                                                    {macroStageTotal(message.toolProgress)}
                                                    {" · "}
                                                    {message.toolProgress.stage === "opportunities" &&
                                                        "Analyzing opportunities"}
                                                    {message.toolProgress.stage === "initiatives" &&
                                                        "Creating initiatives"}
                                                    {message.toolProgress.stage === "persist" &&
                                                        "Saving and exporting"}
                                                    {message.toolProgress.stage === "complete" &&
                                                        "Finishing up"}
                                                    {![
                                                        "opportunities",
                                                        "initiatives",
                                                        "persist",
                                                        "complete",
                                                    ].includes(message.toolProgress.stage) &&
                                                        message.toolProgress.stage}
                                                    {" · "}
                                                    {message.toolProgress.completed}/
                                                    {message.toolProgress.total}
                                                    {message.toolProgress.detail
                                                        ? ` — ${message.toolProgress.detail}`
                                                        : ""}
                                                </Typography>
                                            </>
                                        ) : (
                                            <>
                                                <LinearProgress
                                                    variant="indeterminate"
                                                    sx={{height: 8, borderRadius: 1}}
                                                />
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{display: "block", mt: 0.75}}
                                                >
                                                    Waiting for progress — this may take several minutes.
                                                </Typography>
                                            </>
                                        )}
                                </Box>
                            )}
                        </Stack>
                    ) : (
                        <Box
                            className="bot-message-content"
                            sx={{
                                color: "text.primary",
                            }}
                        >
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={components}
                            >
                                {message.isTransitioning && message.previousContent
                                    ? message.previousContent
                                    : message.content}
                            </ReactMarkdown>
                        </Box>
                    )}
                    {message.initiativeProposal && (
                        <InitiativeDraftCard
                            proposal={message.initiativeProposal}
                            messageId={message.id}
                            superseded={message.initiativeSuperseded}
                        />
                    )}

                    {/* Feedback buttons for agent messages */}
                    {!isUser && !isFunction && (
                        <Box
                            className="feedback-buttons"
                            sx={{
                                display: "flex",
                                gap: 0.5,
                                mt: 0.5,
                            }}
                        >
                            <Tooltip title={copied ? "Copied!" : "Copy"}>
                                <IconButton onClick={handleCopy} size="small">
                                    {copied ? (
                                        <Check sx={{fontSize: 16}}/>
                                    ) : (
                                        <ContentCopyOutlined sx={{fontSize: 16}}/>
                                    )}
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Good response">
                                <IconButton
                                    onClick={handleThumbsUp}
                                    size="small"
                                    disabled={currentSession === null}
                                    color={feedbackGiven === "up" ? "success" : "default"}
                                    loading={feedbackUpLoading}
                                >
                                    <ThumbUpOutlined sx={{fontSize: 16}}/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Bad response">
                                <IconButton
                                    onClick={handleThumbsDown}
                                    size="small"
                                    disabled={currentSession === null}
                                    color={feedbackGiven === "down" ? "warning" : "default"}
                                    loading={feedbackDownLoading}
                                >
                                    <ThumbDownOutlined sx={{fontSize: 16}}/>
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                </Box>
            </Stack>
            <FeedbackModal
                open={feedbackModalOpen}
                onClose={() => setFeedbackModalOpen(false)}
                onSubmit={handleFeedbackSubmit}
            />
        </Box>
    );
});
